/* Sprung-Navigation: weiches Scrollen mit Header-Ausgleich + aktive Pill mitfuehren.

   Als Custom Element gebaut und nicht als Skript mit Listenern am document:
   Horizon rendert Sections im Theme-Editor komplett neu, dabei wuerden an den
   alten Knoten haengende Listener verloren gehen. Ein Custom Element wird vom
   Browser bei jedem Einfuegen in das DOM automatisch neu initialisiert.

   Attribute am Element:
     data-smooth  "true" | "false"  weiches Scrollen inkl. Header-Ausgleich
     data-spy     "true" | "false"  aktive Pill automatisch mitfuehren
     data-offset  Zahl              zusaetzlicher Abstand in px oberhalb des Ziels */

if (!customElements.get('hoell-pill-nav')) {
  class HoellPillNav extends HTMLElement {
    connectedCallback() {
      this.smooth = this.dataset.smooth !== 'false';
      this.spy = this.dataset.spy !== 'false';
      this.extraOffset = parseFloat(this.dataset.offset || '0') || 0;

      this.onClick = this.handleClick.bind(this);
      this.addEventListener('click', this.onClick);

      if (this.spy) {
        this.onScroll = this.requestUpdate.bind(this);
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });
        this.requestUpdate();
      }

      /* Landet jemand direkt auf /seite#anker, hat der Browser schon gesprungen —
         und zwar unter den klebenden Header. Einmal nachkorrigieren. */
      if (this.smooth && window.location.hash) {
        const target = this.targetFor(window.location.hash);
        if (target) {
          requestAnimationFrame(() => this.scrollToTarget(target, 'auto'));
        }
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      if (this.onScroll) {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
      }
      if (this.frame) cancelAnimationFrame(this.frame);
    }

    /** Alle Pills, die auf einen Anker derselben Seite zeigen. */
    get anchorLinks() {
      return Array.from(this.querySelectorAll('a[href*="#"]')).filter((link) => {
        const href = link.getAttribute('href') || '';
        return href.startsWith('#') && href.length > 1;
      });
    }

    targetFor(hash) {
      if (!hash || hash.length < 2) return null;
      let id;
      try {
        id = decodeURIComponent(hash.slice(1));
      } catch (e) {
        id = hash.slice(1);
      }
      /* CSS.escape, weil eine Anker-ID aus dem Theme-Editor mit einer Ziffer
         beginnen oder Sonderzeichen enthalten darf — dann waere '#2-schritte'
         kein gueltiger Selektor. */
      const escaped = window.CSS && CSS.escape ? CSS.escape(id) : id;
      return document.getElementById(id) || document.querySelector('[name="' + escaped + '"]');
    }

    /** Klebende Header sind unterschiedlich hoch — der Wert kommt aus dem Theme. */
    get headerOffset() {
      const raw = getComputedStyle(document.body).getPropertyValue('--header-group-height');
      return (parseFloat(raw) || 0) + this.extraOffset;
    }

    scrollToTarget(target, behavior) {
      const top = target.getBoundingClientRect().top + window.scrollY - this.headerOffset;
      window.scrollTo({ top: Math.max(0, top), behavior: behavior || 'smooth' });
    }

    handleClick(event) {
      if (!this.smooth) return;

      const link = event.target.closest('a[href^="#"]');
      if (!link || !this.contains(link)) return;

      const target = this.targetFor(link.getAttribute('href'));
      if (!target) return;

      event.preventDefault();

      const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.scrollToTarget(target, reduce ? 'auto' : 'smooth');

      /* pushState statt location.hash: ein Hash-Wechsel wuerde den Browser
         zusaetzlich springen lassen und das weiche Scrollen abbrechen. */
      if (window.history && history.pushState) {
        history.pushState(null, '', link.getAttribute('href'));
      }

      this.markActive(link);
      target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    }

    markActive(activeLink) {
      this.anchorLinks.forEach((link) => {
        if (link === activeLink) {
          link.setAttribute('aria-current', 'true');
        } else {
          link.removeAttribute('aria-current');
        }
      });
    }

    requestUpdate() {
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.update());
    }

    /* Aktiv ist der letzte Abschnitt, dessen Oberkante die Lesekante schon
       passiert hat. Bewusst kein IntersectionObserver: der beantwortet
       "sichtbar ja/nein", nicht "welcher Abschnitt liegt gerade oben". */
    update() {
      this.frame = null;
      const links = this.anchorLinks;
      if (!links.length) return;

      const line = this.headerOffset + 8;
      const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
      let active = null;

      links.forEach((link) => {
        const target = this.targetFor(link.getAttribute('href'));
        if (!target) return;
        if (atBottom) {
          active = link;
        } else if (target.getBoundingClientRect().top - line <= 0) {
          active = link;
        }
      });

      this.markActive(active);
    }
  }

  customElements.define('hoell-pill-nav', HoellPillNav);
}
